// Apps, API tokens and webhooks (specs 30).
import { useQuery } from "@tanstack/react-query";
import { api, storePath } from "./api";

export type Token = {
  id: string; kind: "admin" | "storefront"; prefix: string; last4: string; scopes: string[]; created_by: string; created_at: string;
  last_used_at: string | null; revoked_at: string | null; value?: string;
};

export type App = {
  id: string; kind: "custom" | "partner"; partner_app_id?: string; name: string; developer_email: string; scopes: string[]; api_version: string;
  client_id: string; status: "active" | "uninstalled"; installed_at: string; uninstalled_at: string | null; created_by: string; created_at: string;
  version: number; tokens: Token[]; webhooks: number;
};

export type Scope = { key: string; description: string; read_only: boolean; special?: boolean };
export type Topic = { name: string; resource: string };
export type Catalog = {
  versions: { name: string; released: string; sunset: string }[]; current: string; scopes: Scope[]; topics: Topic[]; api_url: string; docs_url: string;
};

export type Subscription = {
  id: string; app_id: string | null; app_name?: string; topic: string; endpoint: string; api_version: string; fields: string[];
  metafield_namespaces: string[]; status: "active" | "disabled"; failure_since: string | null; disabled_at: string | null;
  created_at: string; updated_at: string; version: number;
};

export type Delivery = {
  id: string; webhook_id: string; event_id: string; topic: string; attempt: number; status_code: number | null; latency_ms: number;
  request_headers: Record<string, string>; request_body?: string; response_body: string; error: string; success: boolean; manual: boolean;
  created_at: string;
};

export type PartnerApp = {
  id: string; org_id: string; name: string; developer_email: string; app_url: string; client_id: string; redirect_urls: string[];
  scopes: string[]; api_version: string; installs: number; created_at: string; version: number; install_url: string; client_secret?: string;
};

export type InstallInfo = {
  name: string; developer_email: string; app_url: string; scopes: { key: string; description: string; write: boolean }[];
  installed: boolean; redirect_uri: string;
};

export function useApiCatalog(storeId: string | undefined) {
  return useQuery({ queryKey: ["store", storeId, "/api-catalog"], enabled: !!storeId, staleTime: Infinity,
    queryFn: () => api<Catalog>(storePath(storeId!, "/api-catalog")) });
}

// Access levels per resource, as the scope picker shows them.
export type Access = "none" | "read" | "write";

export function accessOf(scopes: string[], resource: string): Access {
  if (scopes.includes("write_" + resource)) return "write";
  if (scopes.includes("read_" + resource)) return "read";
  return "none";
}

export function withAccess(scopes: string[], resource: string, a: Access): string[] {
  const rest = scopes.filter((s) => s !== "read_" + resource && s !== "write_" + resource);
  return a === "none" ? rest : [...rest, `${a}_${resource}`];
}

// resourceOf: "read_products" -> "products"; special scopes stay whole.
export const resourceOf = (scope: string) => scope.replace(/^(read|write)_/, "");

// Resource groups as the access matrix shows them.
export const RESOURCE_GROUPS: { id: string; resources: string[] }[] = [
  { id: "catalog", resources: ["products", "inventory", "locations", "metaobjects", "files"] },
  { id: "orders", resources: ["orders", "draft_orders", "fulfillments", "returns", "shipping"] },
  { id: "customers", resources: ["customers"] },
  { id: "marketing", resources: ["discounts", "gift_cards"] },
  { id: "store", resources: ["content", "themes", "translations", "markets"] },
  { id: "analytics", resources: ["reports"] },
];

export const SPECIAL_SCOPES: Record<string, string> = { read_all_orders: "all_orders", read_customer_pii: "customer_pii" };

// topicGroup is the resource a topic belongs to ("" for store-wide ones).
export const topicGroup = (tp: Topic) => tp.resource || "store";

// Every topic has a label ("orders/create" → "wh.t.orders/create").
export const topicKey = (name: string) => `wh.t.${name}`;
